import { useEffect, useRef, useState } from "react";

const TURNSTILE_PAGE_URL =
  import.meta.env.VITE_DEPLOYMENT_ENV === "staging"
    ? "https://staging.pulse-date.com/turnstile.html"
    : "https://app.pulse-date.com/turnstile.html";

const TURNSTILE_ORIGIN =
  import.meta.env.VITE_DEPLOYMENT_ENV === "staging"
    ? "https://staging.pulse-date.com"
    : "https://app.pulse-date.com";

type TurnstileMessage =
  | { type: "turnstile"; status: "verified"; token: string }
  | { type: "turnstile"; status: "error"; error?: string }
  | { type: "turnstile"; status: "expired" }
  | { type: "turnstile"; status: "resize"; height: number };

export default function CaptchaFrame({
  onVerify,
  onError,
}: {
  onVerify: (token: string) => void;
  onError?: () => void;
}) {
  const [height, setHeight] = useState(72);

  // Keep the latest callbacks in refs so the message listener is registered
  // once and never goes stale or re-subscribes.
  const onVerifyRef = useRef(onVerify);
  const onErrorRef = useRef(onError);
  useEffect(() => {
    onVerifyRef.current = onVerify;
    onErrorRef.current = onError;
  });

  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      if (event.origin !== TURNSTILE_ORIGIN) return;
      const data = event.data as TurnstileMessage | undefined;
      if (!data || data.type !== "turnstile") return;

      switch (data.status) {
        case "verified":
          onVerifyRef.current(data.token);
          break;
        case "error":
        case "expired":
          onErrorRef.current?.();
          break;
        case "resize":
          if (typeof data.height === "number" && data.height > 0) {
            setHeight(data.height);
          }
          break;
      }
    }

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  return (
    <iframe
      title="captcha"
      src={TURNSTILE_PAGE_URL}
      scrolling="no"
      style={{
        width: "100%",
        height,
        border: "none",
        background: "hsl(240 10% 3.9%)",
        colorScheme: "dark",
      }}
    />
  );
}
